import { useEffect, useMemo, useState } from "react";
import styles from "./style";

function pad(n) {
    return String(n).padStart(2, "0");
}

export function MaintenancePage({ to }) {
    // `to` prop — ISO string e.g. "2026-06-24T02:00:00"
    const [timeLeft, setTimeLeft] = useState(null);

    const toDate = useMemo(() => new Date(to), [to]);

    useEffect(() => {
        // Google Fonts inject
        const link = document.createElement("link");
        link.href = "https://fonts.googleapis.com/css2?family=Nunito:wght@400;500;600;700;800&display=swap";
        link.rel = "stylesheet";
        document.head.appendChild(link);

        // Blink keyframe
        const style = document.createElement("style");
        style.textContent = `@keyframes blink { 0%,100%{opacity:1} 50%{opacity:0.3} }`;
        document.head.appendChild(style);

        function calc() {
            const diff = toDate - new Date();
            if (diff <= 0) {
                setTimeLeft({ h: 0, m: 0, s: 0, done: true });
                return;
            }
            const h = Math.floor(diff / 3600000);
            const m = Math.floor((diff % 3600000) / 60000);
            const s = Math.floor((diff % 60000) / 1000);
            setTimeLeft({ h, m, s, done: false });
        }

        calc();
        const timer = setInterval(calc, 1000);
        return () => clearInterval(timer);
    }, [toDate]);

    const toStr = toDate.toLocaleDateString("en-IN", {
        weekday: "short", month: "short", day: "numeric"
    }) + " · " + toDate.toLocaleTimeString("en-IN", {
        hour: "2-digit", minute: "2-digit", hour12: false,
    });

    return (
        <div style={styles.page}>
            <div style={styles.blob1} />
            <div style={styles.blob2} />

            <div style={styles.wrapper}>
                {/* Logo */}
                <div style={styles.logo}>
                    <span style={styles.logoText}>KTPL</span>
                </div>

                {/* Card */}
                <div style={styles.card}>
                    <div style={styles.badge}>
                        <span style={styles.dot} />
                        Maintenance Mode Active
                    </div>

                    <h1 style={styles.h1}>
                        We're doing a bit of{" "}
                        <span style={{ color: "#3b6ef8" }}>work</span>{" "}
                        behind the scenes.
                    </h1>
                    <div style={styles.divider} />

                    <p style={styles.message}>
                        Our team is currently upgrading the system to bring you a{" "}
                        <strong style={{ color: "#1e2a4a" }}>faster, smoother experience.</strong>
                        <br />
                        We'll be back before you know it — hang tight!
                    </p>

                    {/* ETA */}
                    <div style={styles.etaRow}>
                        <div style={styles.etaIcon}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                                stroke="#3b6ef8" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="12" cy="12" r="10" />
                                <polyline points="12 6 12 12 16 14" />
                            </svg>
                        </div>
                        <div style={styles.etaText}>
                            <div style={styles.etaLabel}>Expected back online</div>
                            <div style={styles.etaValue}>{toStr} IST</div>
                        </div>
                    </div>

                    {/* Countdown */}
                    {timeLeft && !timeLeft.done && (
                        <div style={styles.countdownRow}>
                            <div style={styles.countBox}>
                                <span style={styles.countNum}>{pad(timeLeft.h)}</span>
                                <span style={styles.countLabel}>Hours</span>
                            </div>
                            <div style={styles.countBox}>
                                <span style={styles.countNum}>{pad(timeLeft.m)}</span>
                                <span style={styles.countLabel}>Minutes</span>
                            </div>
                            <div style={styles.countBox}>
                                <span style={styles.countNum}>{pad(timeLeft.s)}</span>
                                <span style={styles.countLabel}>Seconds</span>
                            </div>
                        </div>
                    )}

                    {timeLeft?.done && (
                        <p style={{ color: "#3b6ef8", fontWeight: 700, fontSize: "0.95rem" }}>
                            Maintenance is ending, please refresh the page.
                        </p>
                    )}
                </div>

                <footer style={styles.footer}>
                    Need urgent help?{" "}
                    <a href="https://ktpldev.atlassian.net/servicedesk/customer/portal/4" target="_blank" rel="noreferrer" style={styles.footerLink}>
                        Contact support
                    </a>
                </footer>
            </div>
        </div>
    );
}